import { Telemetry } from '@ctw/shared/context/telemetry';
import { tw } from '@ctw/shared/utils/tailwind';
import { Theme as RadixUiTheme } from '@radix-ui/themes';
import type { RouterConfig } from '@react-types/shared';
import { trim, trimStart, uniqueId } from 'lodash-es';
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

export type RouterOptions = Parameters<ReturnType<typeof useZuiContext>['navigate']>[1];

export type ZusService = 'standalone' | 'zap' | 'smart-on-fhir';

export const ZapVersions = ['v1', 'v2'] as const;
export type ZapVersion = (typeof ZapVersions)[number];

export const isZapVersion = (ZuiVersion: string | null | undefined): ZuiVersion is ZapVersion =>
  ZapVersions.includes(ZuiVersion as never);

type ZuiPresence =
  | 'legacyMainNavigationBar'
  | 'mainNavigationBar'
  | 'subNavigationBar'
  | 'dockedDrawer'
  | 'loadingSpinner';

type ZuiPresences = {
  [key in ZuiPresence]: boolean;
};

type ZuiPresenceRegistrations = {
  [key in ZuiPresence]: Array<string>;
};

const defaultZuiPresences: ZuiPresences = {
  dockedDrawer: false,
  legacyMainNavigationBar: false,
  mainNavigationBar: false,
  subNavigationBar: false,
  loadingSpinner: false,
} as const;

const defaultZuiPresenceRegistrations: ZuiPresenceRegistrations = {
  dockedDrawer: [],
  legacyMainNavigationBar: [],
  mainNavigationBar: [],
  subNavigationBar: [],
  loadingSpinner: [],
} as const;

// Presence state lives at module scope so `usePresence` can subscribe directly via
// `useSyncExternalStore` without going through React context, which means a change to
// a single presence key only re-renders subscribers of that hook, not every `useZuiContext()`
// consumer.
const presenceState: ZuiPresences = { ...defaultZuiPresences };
const presenceRegistrations: ZuiPresenceRegistrations = { ...defaultZuiPresenceRegistrations };
const presenceEventTarget = new EventTarget();
const PRESENCE_EVENT = 'change';

const registerPresence = (presence: ZuiPresence): (() => void) => {
  const presenceId = uniqueId();
  presenceRegistrations[presence].push(presenceId);

  if (presenceRegistrations[presence].length === 1) {
    presenceState[presence] = true;
    presenceEventTarget.dispatchEvent(new Event(PRESENCE_EVENT));
  }

  return () => {
    const index = presenceRegistrations[presence].indexOf(presenceId);
    if (index !== -1) {
      presenceRegistrations[presence].splice(index, 1);
    }

    if (presenceRegistrations[presence].length === 0) {
      presenceState[presence] = false;
      presenceEventTarget.dispatchEvent(new Event(PRESENCE_EVENT));
    }
  };
};

const subscribeToPresenceChanges = (callback: () => void) => {
  presenceEventTarget.addEventListener(PRESENCE_EVENT, callback);
  return () => presenceEventTarget.removeEventListener(PRESENCE_EVENT, callback);
};

type ZuiEventType = 'beforeNavigate' | 'afterNavigate' | 'beforeUrlChange' | 'afterUrlChange';

interface ZuiEventPayloads extends Record<ZuiEventType, Record<string, unknown>> {
  beforeNavigate: {
    from: {
      path: string;
      search: URLSearchParams;
    };
    to: {
      path: string;
      search: URLSearchParams;
    };
  };
  afterNavigate: {
    from: {
      path: string;
      search: URLSearchParams;
    };
    to: {
      path: string;
      search: URLSearchParams;
    };
  };
  beforeUrlChange: {
    from: {
      path: string;
      search: URLSearchParams;
    };
    to: {
      path: string;
      search: URLSearchParams;
    };
  };
  afterUrlChange: {
    from: {
      path: string;
      search: URLSearchParams;
    };
    to: {
      path: string;
      search: URLSearchParams;
    };
  };
}

interface ZuiEvent<EventName extends ZuiEventType = ZuiEventType> {
  name: ZuiEventType;
  payload: ZuiEventPayloads[EventName];
}

interface ZuiState {
  service: string;
  navigate: ZuiProviderProps['navigate'];
  onZuiEvent: <TEventName extends ZuiEventType>(event: ZuiEvent<TEventName>) => void;
  registerZuiEventListener: <TEventName extends ZuiEventType>(
    eventName: TEventName,
    handler: (event: ZuiEvent<TEventName>) => void,
  ) => () => void;
  isNavigating: boolean;
  pathname: string;
}

export const ZuiContext = createContext<ZuiState | null>(null);

export const useZuiContext = () => {
  const context = useContext(ZuiContext);

  if (!context) {
    throw new Error('useZuiContext must be used within a ZuiProvider');
  }

  return context;
};

/**
 * Register that a presence is on-screen until the comsuming component is
 * unmounted, or when `enabled` is set to `false`.
 */
export const useRegisterPresence = (presence: ZuiPresence, enabled = true): void => {
  useLayoutEffect(() => {
    if (!enabled) {
      return undefined;
    }
    return registerPresence(presence);
  }, [presence, enabled]);
};

export const usePresence = (presence: ZuiPresence): boolean => {
  const getSnapshot = useCallback(() => presenceState[presence], [presence]);
  return useSyncExternalStore(subscribeToPresenceChanges, getSnapshot, () => false);
};

interface ZuiProviderProps extends PropsWithChildren {
  service: ZusService | 'storybook' | 'playwright';
  navigate: (path: string, routerOptions?: RouterConfig['routerOptions'] | undefined) => void;
  navigateBasePath?: string;
  navigatePersistentQueryParams?: Array<string>;
  pathname: () => string | { pathname: string };
}

export const ZuiProvider = ({
  service,
  navigate,
  navigateBasePath,
  navigatePersistentQueryParams,
  pathname: usePathname,
  children,
}: ZuiProviderProps) => {
  const pathname = usePathname();
  const [isNavigating, setIsNavigating] = useState(false);
  const isNavigatingRef = useRef(false);
  const pendingNavigationRef = useRef<{
    payload: ZuiEventPayloads['afterNavigate'];
    triggerNavigationEvents: boolean;
  } | null>(null);
  const [eventListeners, setEventListeners] = useState<
    Record<
      Parameters<ZuiState['registerZuiEventListener']>[0],
      Array<Parameters<ZuiState['registerZuiEventListener']>[1]>
    >
  >({
    beforeNavigate: [],
    afterNavigate: [],
    beforeUrlChange: [],
    afterUrlChange: [],
  });

  const onZuiEvent = useCallback(
    (event: ZuiEvent) => {
      for (const listener of eventListeners[event.name]) {
        listener(event);
      }
    },
    [eventListeners],
  );

  // Detect navigation completion reactively via URL changes from the router.
  // Include search so search-only navigations also trigger the completion effect —
  // `resolvedPathname` alone misses `/a?x=0 → /a?x=1`. React Router's `useLocation()`
  // provides `search` on the object form; Next.js's `usePathname()` returns a plain
  // string and doesn't convey search, so search-only navs there still won't trigger
  // this effect (that would require threading `useSearchParams()` through the prop).
  const resolvedPathname = typeof pathname === 'string' ? pathname : pathname.pathname;
  const resolvedSearch =
    typeof pathname === 'string' ? '' : ((pathname as { search?: string }).search ?? '');
  const resolvedUrl = `${resolvedPathname}${resolvedSearch}`;
  // biome-ignore lint/correctness/useExhaustiveDependencies: `resolvedUrl` is intended to cause a re-render. `onZuiEvent` is excluded so that listener registrations don't cause this effect to re-run and prematurely complete an in-flight pending navigation.
  useEffect(() => {
    const pending = pendingNavigationRef.current;
    if (!pending) {
      return;
    }

    pendingNavigationRef.current = null;
    isNavigatingRef.current = false;

    onZuiEvent({
      name: 'afterUrlChange',
      payload: pending.payload,
    });

    if (pending.triggerNavigationEvents) {
      Telemetry.afterNavigate();
      onZuiEvent({
        name: 'afterNavigate',
        payload: pending.payload,
      });
      setIsNavigating(false);
    }
  }, [resolvedUrl]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: No explanation was provided. Please be a good citizen and add one!
  const zuiContext: ZuiState = useMemo(
    () => ({
      service,
      navigate: (requestedPath, routerOptions) => {
        // Use a ref for guarding navigation to avoid stale closures and to prevent recreating the
        // context on every navigation
        if (isNavigatingRef.current) {
          return;
        }

        // Save the pathname and search params from the current URL before navigating
        const pathnameBeforeNavigation = window.location.pathname;
        const searchStringBeforeNavigation = window.location.search;

        // Try to parse `requestedPath` into a pathname and search params
        let requestedPathname: string;
        let requestedSearchParams: URLSearchParams | undefined;
        try {
          const newUrl = new URL(
            requestedPath,
            `${window.location.protocol}//${window.location.host}`,
          );
          requestedPathname = newUrl.pathname;
          requestedSearchParams = newUrl.searchParams;
        } catch {
          // Failure to parse `requestedPath` means the path was invalid, so we can't navigate to it
          return;
        }

        // Construct our destination URL
        let destinationUrl: URL | undefined;
        try {
          const destinationUrlComponents = [
            trim(navigateBasePath, '/') /* The base path the `ZuiProvider` is configured with */,
            trimStart(requestedPathname, '/') /* The requested pathname */,
          ];
          destinationUrl = new URL(
            destinationUrlComponents.join('/'),
            `${window.location.protocol}//${window.location.host}`,
          );

          // If `ZuiProvider` was provided a `navigatePersistentQueryParams` prop, we will always copy
          // those search params from the current URL (if present) to the destination search params
          if (navigatePersistentQueryParams) {
            const currentUrl = new URL(window.location.href);
            for (const param of navigatePersistentQueryParams) {
              const value = currentUrl.searchParams.get(param);
              if (value) {
                destinationUrl.searchParams.set(param, value);
              }
            }
          }

          // If the requested path included query params, we will also copy those to the destination
          for (const [param, value] of requestedSearchParams.entries()) {
            destinationUrl.searchParams.set(param, value);
          }
        } catch {
          // If we fail to construct the destination URL, we can't navigate to it
          return;
        }

        // Disallow navigating to the current URL (same pathname and search params, includes the `?` prefix).
        if (
          destinationUrl.pathname === pathnameBeforeNavigation &&
          destinationUrl.search === searchStringBeforeNavigation
        ) {
          return;
        }

        const requestedPathIsAbsolute =
          requestedPath.startsWith('/') || requestedPath.includes('://');

        // Construct the destination path we will pass to the underlying `navigate` implementation
        const destinationPathname = requestedPathIsAbsolute
          ? destinationUrl.pathname
          : trimStart(destinationUrl.pathname, '/');
        const destinationPath = `${destinationPathname}${destinationUrl.search}`;

        // Only trigger navigation events for real page changes; this ensures that we don't trigger
        // navigation events for things like opening a drawer with a deep link, which would cause
        // unnecessary UI churn and potentially extra loading indicators.
        const triggerNavigationEvents = requestedPathIsAbsolute
          ? destinationPathname !== pathnameBeforeNavigation
          : destinationPathname !== trimStart(pathnameBeforeNavigation, '/');

        const navigateEventPayload = {
          from: {
            path: pathnameBeforeNavigation,
            search: new URLSearchParams(searchStringBeforeNavigation),
          },
          to: {
            path: destinationPathname,
            search: new URLSearchParams(destinationUrl.search),
          },
        };

        onZuiEvent({
          name: 'beforeUrlChange',
          payload: navigateEventPayload,
        });

        // Trigger the `beforeNavigate` event and set the `isNavigating` state to `true`
        if (triggerNavigationEvents) {
          onZuiEvent({
            name: 'beforeNavigate',
            payload: navigateEventPayload,
          });
          isNavigatingRef.current = true;
          setIsNavigating(true);
        }

        Telemetry.beforeNavigate();

        // Store the pending navigation so the pathname-change useEffect can complete it.
        // Navigation completion is detected reactively when the router updates the pathname
        // (via usePathname in Next.js or useLocation in React Router).
        pendingNavigationRef.current = {
          payload: navigateEventPayload,
          triggerNavigationEvents,
        };

        // Perform the actual navigation
        navigate(destinationPath, { scroll: false, ...routerOptions } as never);
      },
      onZuiEvent,
      registerZuiEventListener: (eventName, listener) => {
        setEventListeners((prev) => ({
          ...prev,
          [eventName]: [...prev[eventName], listener],
        }));

        return () =>
          setEventListeners((prev) => ({
            ...prev,
            [eventName]: prev[eventName].filter((l) => l !== listener),
          }));
      },
      isNavigating,
      pathname: typeof pathname === 'string' ? pathname : pathname.pathname,
    }),
    [service, isNavigating, pathname, navigatePersistentQueryParams, navigateBasePath],
  );

  return (
    // https://www.radix-ui.com/themes/docs/components/theme
    <RadixUiTheme
      className={tw`flex h-[100vh] max-h-[100vh] w-[100vw] max-w-[100vw] flex-col overflow-hidden`}
      hasBackground={false}
      appearance="light"
      panelBackground="translucent"
      radius="medium"
      scaling="100%"
      accentColor="green"
      grayColor="gray"
    >
      <ZuiContext.Provider value={zuiContext}>{children}</ZuiContext.Provider>
    </RadixUiTheme>
  );
};
